import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFormatCurrency } from "@/context/CountryContext";
import { BedDouble, MapPin, Phone, Mail, Star, Wifi, Car, Waves, Utensils } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const AMENITY_ICONS = { wifi: "📶", piscine: "🏊", parking: "🅿️", clim: "❄️", restaurant: "🍽️", spa: "💆", petit_déjeuner: "☕", cuisine_équipée: "🍳", sécurité_24h: "🔒", cuisine: "🍳" };
const TYPE_LABEL = { hotel: "Hôtel", auberge: "Auberge", residence: "Résidence", chambre: "Chambre", villa: "Villa" };

export default function Hebergements() {
  const fmt = useFormatCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [chambres, setChambres] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/hebergements").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const openItem = async (h) => {
    setSelected(h);
    const res = await api.get(`/hebergements/${h.hebergement_id}`);
    setChambres(res.data.chambres || []);
  };

  const types = ["all", ...new Set(items.map((i) => i.type).filter(Boolean))];
  const filtered = typeFilter === "all" ? items : items.filter((i) => i.type === typeFilter);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Chargement…</div>;

  if (selected) return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button onClick={() => setSelected(null)} className="text-sm text-primary mb-4 hover:underline">← Retour</button>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="h-48 bg-secondary/80 relative overflow-hidden">
          {(selected.cover_url || selected.logo_url) ? (
            <img src={selected.cover_url || selected.logo_url} alt={selected.name}
              className="w-full h-full object-cover" onError={e => { e.target.style.display="none"; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BedDouble className="w-16 h-16 text-white/30" />
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs text-muted-foreground mb-1">{TYPE_LABEL[selected.type] || selected.type}</div>
              <h1 className="font-display text-2xl font-bold text-secondary">{selected.name}</h1>
            </div>
            <div className="text-right">
              {selected.rating > 0 && <div className="flex items-center gap-1 text-amber-500 font-semibold"><Star className="w-4 h-4 fill-amber-400" />{selected.rating}</div>}
              <div className="font-display font-bold text-primary text-xl mt-1">
                À partir de {fmt(selected.price_from)}<span className="text-sm font-normal">/nuit</span>
              </div>
            </div>
          </div>
          <p className="mt-3 text-muted-foreground">{selected.description}</p>
          <div className="mt-4 space-y-2 text-sm text-muted-foreground">
            {selected.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" />{selected.address}</div>}
            {selected.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 shrink-0" />{selected.phone}</div>}
            {selected.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 shrink-0" />{selected.email}</div>}
          </div>
          {selected.amenities?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selected.amenities.map((a) => (
                <span key={a} className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
                  {AMENITY_ICONS[a] || "•"} {a}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {chambres.length > 0 && (
        <div className="mt-6">
          <h2 className="font-display text-lg font-bold text-secondary mb-3">Chambres disponibles</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {chambres.map((c) => (
              <div key={c.chambre_id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium text-secondary">{c.nom}</div>
                    <div className="text-xs text-muted-foreground capitalize mt-0.5">{c.type} · {c.capacite} pers.</div>
                  </div>
                  <div className="font-display font-bold text-primary text-right">
                    {fmt(c.prix_nuit)}<br/><span className="text-xs font-normal text-muted-foreground">/nuit</span>
                  </div>
                </div>
                {c.description && <p className="text-xs text-muted-foreground mt-2">{c.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-secondary flex items-center gap-2">
            <BedDouble className="w-7 h-7 text-primary" /> Hébergements
          </h1>
          <p className="text-muted-foreground mt-1">Hôtels, villas, résidences et chambres à Abidjan.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {types.map((t) => (
            <button key={t} onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${typeFilter === t ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
              {t === "all" ? "Tous" : TYPE_LABEL[t] || t}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">Aucun hébergement trouvé.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((h) => (
            <div key={h.hebergement_id} onClick={() => openItem(h)}
              className="bg-card border border-border rounded-2xl overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/30 transition-all">
              <div className="h-36 bg-secondary/10 relative overflow-hidden">
                {(h.logo_url || h.cover_url) ? (
                  <img src={h.logo_url || h.cover_url} alt={h.name}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }} />
                ) : null}
                <div className={`w-full h-full ${(h.logo_url || h.cover_url) ? "hidden" : "flex"} items-center justify-center absolute inset-0`}>
                  <BedDouble className="w-10 h-10 text-secondary/20" />
                </div>
              </div>
              <div className="p-4">
                <div className="text-xs text-muted-foreground mb-1">{TYPE_LABEL[h.type] || h.type}</div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold text-secondary leading-tight">{h.name}</h3>
                  {h.rating > 0 && <div className="flex items-center gap-0.5 text-amber-500 text-xs font-semibold shrink-0"><Star className="w-3.5 h-3.5 fill-amber-400" />{h.rating}</div>}
                </div>
                {h.amenities?.slice(0, 3).map((a) => <span key={a} className="text-xs">{AMENITY_ICONS[a] || ""} </span>)}
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{h.city}</div>
                  <div className="font-display font-bold text-primary text-sm">{fmt(h.price_from)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
